import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Editor } from '@tiptap/core';
import { osmosEditorExtensions } from './osmos-editor.schema';
import { OsmosEditorToolbarComponent } from './osmos-editor-toolbar.component';

export type OsmosEditorToolbarMode = 'full' | 'none';

/**
 * The Osmos rich-text editor.
 *
 * Drop-in for the CKEditor it replaces: it implements ControlValueAccessor,
 * so it binds through either `formControlName` or `[(ngModel)]` with no other
 * changes at the call site.
 *
 * What it may produce is defined entirely by osmos-editor.schema.ts.
 */
@Component({
  selector: 'osmos-editor',
  standalone: true,
  imports: [CommonModule, OsmosEditorToolbarComponent],
  templateUrl: './osmos-editor.component.html',
  styleUrls: ['./osmos-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OsmosEditorComponent),
      multi: true,
    },
  ],
})
export class OsmosEditorComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
  /** 'none' gives a chromeless inline surface — used on the quote screen. */
  @Input() toolbar: OsmosEditorToolbarMode = 'full';
  @Input() placeholder = '';

  @ViewChild('content', { static: true }) contentEl!: ElementRef<HTMLDivElement>;

  editor: Editor | null = null;

  private pendingValue = '';
  private lastEmitted = '';
  private disabled = false;
  private viewDestroyed = false;

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    // ProseMirror fires an event per keystroke. Creating the editor inside
    // the Angular zone runs full change detection on every key, which makes
    // the three-language item form crawl. Stay outside it and re-enter only
    // to hand a value back to the form.
    this.zone.runOutsideAngular(() => {
      this.editor = new Editor({
        element: this.contentEl.nativeElement,
        extensions: osmosEditorExtensions(),
        content: this.pendingValue,
        editable: !this.disabled,
        // emit() re-enters the Angular zone, which runs a normal change
        // detection pass and repaints the toolbar with it. Calling
        // refreshToolbar() here as well nests detectChanges() inside that pass
        // and throws NG0100 in any parent binding on the same value —
        // measured in the harness 2026-09-04.
        onUpdate: () => this.emit(),
        // A caret move changes no content, so it never reaches emit() and
        // never enters the zone. Without this explicit repaint the toolbar's
        // active states go stale: Bold stays lit after the caret leaves bold
        // text, and the block picker keeps naming the previous block.
        // Measured in the browser harness 2026-09-04.
        onSelectionUpdate: ({ transaction }) => {
          // A transaction that also changed the document already goes through
          // emit(), which re-enters the zone and repaints. Repainting here as
          // well nests detectChanges() inside that pass and throws NG0100 in
          // any parent binding on the same value.
          if (transaction.docChanged) {
            return;
          }
          this.refreshToolbar();
        },
        onBlur: () => this.zone.run(() => this.onTouched()),
      });
    });

    // The toolbar renders only once the editor instance exists.
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.viewDestroyed = true;
    this.editor?.destroy();
    this.editor = null;
  }

  /**
   * Repaints this component's view only — not the whole application — so the
   * toolbar's active states follow the caret while the editor itself stays
   * outside the Angular zone. Skipped entirely in chromeless mode, where there
   * is no toolbar to update.
   */
  private refreshToolbar(): void {
    if (this.viewDestroyed || this.toolbar === 'none' || !this.editor) {
      return;
    }
    this.cdr.detectChanges();
  }

  /** Tiptap reports an empty document as `<p></p>`; the form must see ''. */
  private currentHtml(): string {
    if (!this.editor) {
      return this.pendingValue;
    }
    return this.editor.isEmpty ? '' : this.editor.getHTML();
  }

  private emit(): void {
    const html = this.currentHtml();
    if (html === this.lastEmitted) {
      return;
    }
    this.lastEmitted = html;
    this.zone.run(() => this.onChange(html));
  }

  // --- ControlValueAccessor ---

  writeValue(value: string | null | undefined): void {
    const html = value ?? '';

    if (!this.editor) {
      this.pendingValue = html;
      this.lastEmitted = html;
      return;
    }

    // Without this guard the form and the editor feed each other in a loop,
    // and every keystroke would reset the cursor to the start of the field.
    if (html === this.currentHtml()) {
      return;
    }

    this.lastEmitted = html;
    this.editor.commands.setContent(html, { emitUpdate: false });
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.editor?.setEditable(!isDisabled);
  }
}
